import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import { adminApi } from '../../api/adminApi';
import { fetchAllPages } from '../../api/fetchAllPages';
import { afterSuccess, invalidateCrmBrokerGroup } from '../../api/crmCaches';

// countryConfig doc id → its data (countryCode, timezone, …). A broker group's `config` is
// a reference to one of these docs (the admin list gives its id).
const getCountryConfigs = async () => {
	const snapshot = await getDocs(
		query(collection(firestore, 'config'), where('type', '==', 'countryConfig')),
	);
	return new Map(snapshot.docs.map((configDoc) => [configDoc.id, configDoc.data()]));
};

/**
 * Admin broker group endpoints. listAllBrokerGroups loads every broker group (for pickers,
 * names and the list of ledgers), cached and shared by everything that needs it. Each item
 * gets from its config doc: countryCode (the config's, or else its stored `country`, like
 * the CRM's broker countryCode), state and timezone (the config's only).
 */
export const adminBrokerGroupsApi = adminApi.injectEndpoints({
	endpoints: (builder) => ({
		listAllBrokerGroups: builder.query({
			queryFn: async (arg, api, extraOptions, baseQuery) => {
				try {
					const [result, countryConfigs] = await Promise.all([
						fetchAllPages(baseQuery, '/broker-groups/list'),
						getCountryConfigs(),
					]);
					if (result.error) {
						return result;
					}
					return {
						data: result.data.map((brokerGroup) => {
							const config = countryConfigs.get(brokerGroup.config);
							return {
								...brokerGroup,
								countryCode: config?.countryCode ?? brokerGroup.country,
								state: config?.state,
								timezone: config?.timezone,
							};
						}),
					};
				} catch (error) {
					console.warn('Could not read the country configs', error);
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
			},
			providesTags: (result) => [
				{ type: 'ADMIN_BROKER_GROUP', id: 'LIST' },
				...(result ?? []).map(({ id }) => ({ type: 'ADMIN_BROKER_GROUP', id })),
			],
			// Fill getBrokerGroup for every broker group, so single readers need no request
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const { data: brokerGroups } = await queryFulfilled;
					dispatch(
						adminBrokerGroupsApi.util.upsertQueryEntries(
							brokerGroups.map((brokerGroup) => ({
								endpointName: 'getBrokerGroup',
								arg: brokerGroup.id,
								value: brokerGroup,
							})),
						),
					);
				} catch {
					// The list query reports its own error
				}
			},
		}),
		// One broker group, as listAllBrokerGroups gives it (with its config details).
		// Normally filled in by the list; if read before the list has loaded (or after an
		// update invalidates it), it loads the list and picks the broker group.
		getBrokerGroup: builder.query({
			queryFn: async (brokerGroupId, { dispatch }) => {
				const { data: brokerGroups, error } = await dispatch(
					adminBrokerGroupsApi.endpoints.listAllBrokerGroups.initiate(undefined, {
						subscribe: false,
					}),
				);
				if (error) {
					return { error };
				}
				return {
					data: brokerGroups.find((brokerGroup) => brokerGroup.id === brokerGroupId) ?? null,
				};
			},
			providesTags: (result, error, brokerGroupId) => [
				{ type: 'ADMIN_BROKER_GROUP', id: brokerGroupId },
			],
		}),
		/**
		 * The countries a broker group can be in: the config collection's `country` docs
		 * (seeded by the backend's scripts/seed-countries.js), keyed by country code, each
		 * { countryName, currency, timezones, states }. The admin API validates against the
		 * same docs.
		 */
		listCountries: builder.query({
			queryFn: async () => {
				try {
					const snapshot = await getDocs(
						query(collection(firestore, 'config'), where('type', '==', 'country')),
					);
					return {
						data: Object.fromEntries(
							snapshot.docs.map((countryDoc) => {
								const {
									countryCode,
									countryName,
									currency,
									timezones = [],
									states = [],
								} = countryDoc.data();
								return [countryCode, { countryName, currency, timezones, states }];
							}),
						),
					};
				} catch (error) {
					console.warn('Could not read the countries', error);
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
			},
		}),
		/**
		 * PATCH /broker-groups/:id with only the changed fields (see the backend's
		 * services/validation/brokerGroup.js). Refreshes the list, which adds each broker
		 * group's config details as it loads. A 400 can carry { fields: { [field]: message } }.
		 */
		updateBrokerGroup: builder.mutation({
			query: ({ id, ...changes }) => ({
				url: `/broker-groups/${id}`,
				method: 'PATCH',
				body: changes,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'ADMIN_BROKER_GROUP', id },
				{ type: 'ADMIN_BROKER_GROUP', id: 'LIST' },
				// The users list carries its broker groups' names and ledgers
				{ type: 'ADMIN_USER', id: 'LIST' },
			],
			// The CRM's own copies of the broker group, too
			onQueryStarted: afterSuccess((dispatch, { id }) => invalidateCrmBrokerGroup(dispatch, id)),
		}),
	}),
});

export const {
	useListAllBrokerGroupsQuery,
	useGetBrokerGroupQuery,
	useListCountriesQuery,
	useUpdateBrokerGroupMutation,
} = adminBrokerGroupsApi;
